import { ArrowRight, CheckCircle, Copy } from "lucide-react";
import { useState } from "react";
import logoBeOneAgency from "@/assets/logo.svg";
import { useT } from "../i18n";
import { ImageWithFallback } from "./figma/ImageWithFallback";

interface PausedScreenProps {
  email: string | null;
  onContinueNow: () => void;
}

export function PausedScreen({ email, onContinueNow }: PausedScreenProps) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const resumeLink = (() => {
    const base = window.location.origin + window.location.pathname;
    return email ? `${base}?email=${encodeURIComponent(email)}` : base;
  })();

  const handleCopy = () => {
    navigator.clipboard.writeText(resumeLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        background: "linear-gradient(to bottom right, #02324A, #00AFFD)",
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl px-[20px] py-12 max-w-lg w-full text-center">
        <ImageWithFallback
          src={logoBeOneAgency}
          alt="Logo"
          width={91}
          height={40}
          className="h-10 mb-6 mx-auto"
        />

        <CheckCircle
          className="w-12 h-12 mx-auto mb-4"
          style={{ color: "#FF00D2" }}
        />

        <h2 className="font-[Roboto_Slab] text-[#02324a] text-2xl mb-3">
          {t("paused.title")}
        </h2>

        <p className="font-[Roboto] text-gray-600 mb-6">{t("paused.body")}</p>

        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-3">
          <span className="font-[Roboto] text-sm text-gray-700 flex-1 truncate text-left">
            {resumeLink}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 flex items-center gap-1 text-sm font-[Roboto] transition-colors"
            style={{ color: copied ? "#22c55e" : "#FF00D2" }}
          >
            {copied ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            {copied ? t("paused.copied") : t("paused.copy")}
          </button>
        </div>

        <p className="font-[Roboto] text-gray-400 text-xs mb-8">
          {t("paused.emailReminder")}
        </p>

        <button
          type="button"
          onClick={onContinueNow}
          className="w-full text-white py-4 px-8 rounded-lg font-medium transition-colors font-[Roboto] flex items-center justify-center gap-2"
          style={{ backgroundColor: "#FF00D2" }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#cc00a8")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#FF00D2")
          }
        >
          {t("paused.continueNow")}
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
